import { Component, EventEmitter, OnDestroy } from '@angular/core';
import { StringUtils } from '@blcloud/bl-common';
import { IDmExtractCondition, ISnsBulkSendHistory, ISnsDeliveryRequest } from '@blcloud/bl-datamodel';
import { SnsFollowStatusDiv } from '@blcloud/bl-datamodel/enum/sns/sns-follow-status-div';
import { DmExtractMenuCode } from '@blcloud/bl-ng-share-module';
import {
  AbstractBlModal,
  BlDialogService,
  ModalButtonColor,
  ModalButtonSetting,
  ModalButtonType,
  ModalReason,
  ModalSize
} from '@blcloud/bl-ng-ui-component';
import { filter as _filter, forEach as _forEach, isNil as _isNil, map as _map } from 'lodash';
import { takeUntil } from 'rxjs/operators';
import { ISnsExtractCondition, ISnsSendMessageInfo } from './sns-send-message-confirm-modal.interface';
import { SnsSendMessageConfirmModalService } from './sns-send-message-confirm-modal.service';

/**
 * SNS一斉送信確認モーダル
 * ```
 * content.data: {
 *   // SNS送信メッセージ情報
 *   snsSendMessageInfo: ISnsSendMessageInfo
 * }
 * ```
 */

@Component({
  templateUrl: './sns-send-message-confirm-modal.component.html',
  styleUrls: ['./sns-send-message-confirm-modal.component.scss'],
  providers: [SnsSendMessageConfirmModalService]
})
export class SnsSendMessageConfirmModalComponent
  extends AbstractBlModal
  implements OnDestroy {

  private readonly onDestroy$ = new EventEmitter();

  /** SNS送信メッセージ情報 */
  _snsSendMessageInfo = <ISnsSendMessageInfo>{};

  /** SNS配信リクエスト */
  _snsDeliveryRequest: ISnsDeliveryRequest;

  /** LINEメッセージプレビューデータ */
  _previewMessages: string[] = [];

  /** リッチ版LINE案内文を設定するか */
  _isSettingLineRichTemplate = false;

  /** ボタンタイプ設定 */
  footerButtonType: ModalButtonType = ModalButtonType.OkCancel;

  /** 決定ボタンの設定 */
  okButtonSetting: ModalButtonSetting = { color: ModalButtonColor.Success, label: '送信' };

  /** 戻るボタンの設定 */
  cancelButtonSetting: ModalButtonSetting = { color: ModalButtonColor.None, label: 'キャンセル' };

  /** コンストラクタ */
  constructor(
    private service: SnsSendMessageConfirmModalService,
    private dialogService: BlDialogService,
  ) {
    super();
  }

  /** @override */
  defaultOption() {
    return { size: ModalSize.Large };
  }

  ngOnDestroy() {
    this.hideLoading();
    this.onDestroy$.emit();
  }

  getResults(): any {
    return {};
  }

  /** 初期表示データ取得 */
  onShown() {
    this._snsSendMessageInfo = this.data.snsSendMessageInfo;
    this.createDmExtractCondition(this._snsSendMessageInfo.snsExtractCondition);
    this.convertSnsDeliveryRequest();
    this.setSettingLineRichTemplate();

    // LINEメッセージプレビュー表示
    this.service.createPreview(this.data.snsSendMessageInfo).subscribe(
      preview => this._previewMessages = preview
    );
  }

  /**
   * DM抽出条件の生成処理
   * @param snsExtractCondition SNS抽出条件情報
   */
  private createDmExtractCondition(snsExtractCondition: ISnsExtractCondition) {
    const result = <IDmExtractCondition>{};
    result.dmExtractConditionItemList = [];
    result.dmExtractConditionItemList.push({
      dmExtractConditionProperty: 'snsPotentialCustomer.snsFollowStatusDiv',
      dmExtractConditionOperator: 'in',
      dmExtractConditionValue: `${SnsFollowStatusDiv.AfterFollow},${SnsFollowStatusDiv.CancelBlock}`,
    });
    // 顧客未登録の友だちのみフラグ
    if (snsExtractCondition.unregisteredFriendsFlg) {
      result.dmExtractConditionItemList.push({
        dmExtractConditionProperty: 'snsPotentialCustomer.customerCode',
        dmExtractConditionOperator: 'eq',
        dmExtractConditionValue: '0',
      });
    }
    // SNS友達登録年月日(開始)
    if (snsExtractCondition.snsFriendRegistDateFrom) {
      result.dmExtractConditionItemList.push({
        dmExtractConditionProperty: 'snsPotentialCustomer.snsFriendRegistDate',
        dmExtractConditionOperator: 'ge',
        dmExtractConditionValue: snsExtractCondition.snsFriendRegistDateFrom
      });
    }
    // SNS友達登録年月日(終了)
    if (snsExtractCondition.snsFriendRegistDateTo) {
      result.dmExtractConditionItemList.push({
        dmExtractConditionProperty: 'snsPotentialCustomer.snsFriendRegistDate',
        dmExtractConditionOperator: 'le',
        dmExtractConditionValue: snsExtractCondition.snsFriendRegistDateTo
      });
    }
    // SNS最終メッセージ受信日時(開始)
    if (snsExtractCondition.snsLastMessageReceiveDateTimeFrom) {
      result.dmExtractConditionItemList.push({
        dmExtractConditionProperty: 'snsPotentialCustomer.snsLastMessageReceiveDateTime',
        dmExtractConditionOperator: 'ge',
        dmExtractConditionValue: snsExtractCondition.snsLastMessageReceiveDateTimeFrom + 'T00:00:00'
      });
    }
    // SNS最終メッセージ受信日時(終了)
    if (snsExtractCondition.snsLastMessageReceiveDateTimeTo) {
      result.dmExtractConditionItemList.push({
        dmExtractConditionProperty: 'snsPotentialCustomer.snsLastMessageReceiveDateTime',
        dmExtractConditionOperator: 'le',
        dmExtractConditionValue: snsExtractCondition.snsLastMessageReceiveDateTimeTo + 'T23:59:59'
      });
    }
    // ラベル
    if (snsExtractCondition.labelCodeList) {
      result.dmExtractConditionItemList.push(
        ...this.getListValue('snsPotentialCustomer.labelCodeList', snsExtractCondition.labelCodeList)
      );
    }
    // チケット関連
    // 有効なクーポンを持っていない顧客
    if (snsExtractCondition.searchNotTicketFlg) {
      result.dmExtractConditionItemList.push({
        dmExtractConditionProperty: 'snsPotentialCustomer.ticketIssueInfoList',
        dmExtractConditionOperator: 'eq',
        dmExtractConditionValue: '{}'
      });
    } else {
      // チケットコード
      if (this.isValid(snsExtractCondition.ticketSettingList)) {
        const ticketCodeList = [];
        // チケットの空白行を除外する
        _forEach(snsExtractCondition.ticketSettingList, (data) => {
          if (!_isNil(data.ticketCode)) {
            ticketCodeList.push(data.ticketCode);
          }
        });
        if (ticketCodeList.length !== 0) {
          result.dmExtractConditionItemList.push({
            dmExtractConditionProperty: 'ticketIssue.ticketCode',
            dmExtractConditionOperator: 'in',
            dmExtractConditionValue: ticketCodeList.toString(),
          });
        }
      }
      // チケット有効期限（開始日）
      // 入力値 <= 有効期限（終了）の為、適用終了日にセット
      if (this.isValid(snsExtractCondition.applyStartDate)) {
        result.dmExtractConditionItemList.push({
          dmExtractConditionProperty: 'ticketIssue.applyEndDate',
          dmExtractConditionOperator: 'ge',
          dmExtractConditionValue: snsExtractCondition.applyStartDate
        });
      }
      // チケット有効期限（終了日）
      // 入力値 >= 有効期限（開始）の為、適用開始日にセット
      if (this.isValid(snsExtractCondition.applyEndDate)) {
        result.dmExtractConditionItemList.push({
          dmExtractConditionProperty: 'ticketIssue.applyStartDate',
          dmExtractConditionOperator: 'le',
          dmExtractConditionValue: snsExtractCondition.applyEndDate
        });
      }
      // チケット発行日
      if (this.isValid(snsExtractCondition.ticketIssueDate)) {
        result.dmExtractConditionItemList.push({
          dmExtractConditionProperty: 'ticketIssue.ticketIssueDate',
          dmExtractConditionOperator: 'ge',
          dmExtractConditionValue: snsExtractCondition.ticketIssueDate
        });
      }
      // チケット有効期限終了日（開始日）
      if (this.isValid(snsExtractCondition.applyEndDateStart)) {
        result.dmExtractConditionItemList.push({
          dmExtractConditionProperty: 'ticketIssue.applyEndDateStart',
          dmExtractConditionOperator: 'ge',
          dmExtractConditionValue: snsExtractCondition.applyEndDateStart
        });
      }
      // チケット有効期限終了日（終了日）
      if (this.isValid(snsExtractCondition.applyEndDateEnd)) {
        result.dmExtractConditionItemList.push({
          dmExtractConditionProperty: 'ticketIssue.applyEndDateEnd',
          dmExtractConditionOperator: 'le',
          dmExtractConditionValue: snsExtractCondition.applyEndDateEnd
        });
      }
    }
    this._snsSendMessageInfo.dmExtractCondition = result;
  }

  /**
   * 複数指定項目の設定
   * @param key 対象Entity
   * @param list 変換前データ配列
   * @returns 変換後のデータ
   */
  private getListValue(key, list) {
    const validDataList = _filter(list, val => {
      return this.isValid(val);
    });
    if (this.isValid(validDataList)) {
      return _map(validDataList, val => {
        return {
          dmExtractConditionProperty: key,
          dmExtractConditionOperator: 'in',
          dmExtractConditionValue: val
        };
      });
    } else {
      return [];
    }
  }

  /**
   * 有効な値か判定する
   * @param value
   * @returns 有効の場合true
   */
  isValid(value: any) {
    if (_isNil(value)) {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    } else {
      if (value === '') {
        return false;
      }
      if (value === 0) {
        return false;
      }
      if (value === '1900-01-01' || value === 19000101) {
        return false;
      }
      if (value === '1900-01-01T00:00:00' || value === 19000101000000) {
        return false;
      }
      if (value === '1900-01' || value === 190001) {
        return false;
      }
    }
    return true;
  }

  /**
   * SNS配信リクエストへの変換処理
   */
  private convertSnsDeliveryRequest() {
    this._snsDeliveryRequest = {
      snsLinkageId: this._snsSendMessageInfo.snsLinkageId,
      snsDiv: this._snsSendMessageInfo.snsDiv,
      dmExtractMenuCode: this._snsSendMessageInfo.dmExtractMenuCode,
      dmExtractMenuDiv: this._snsSendMessageInfo.dmExtractMenuDiv,
      dmExtractCondition: this._snsSendMessageInfo.dmExtractCondition,
      dmContentNumber: this._snsSendMessageInfo.dmContentNumber,
      dmContentName: this._snsSendMessageInfo.dmContentName,
      dmContentTitle: this._snsSendMessageInfo.dmContentTitle,
      messageContents: this._snsSendMessageInfo.dmContentBody,
      lineMsgTemplateType: this._snsSendMessageInfo.lineMsgTemplateType,
      ticketCodeList: this._snsSendMessageInfo.ticketCodeList,
      snsSendTitle: this._snsSendMessageInfo.snsSendTitle,
      dmSnsMsgSettingList: this._snsSendMessageInfo.dmContentInfo.dmSnsMsgSettingList,
    };
  }

  /**
   * SNS一斉送信処理
   * @param reason
   */
  private broadcastSns(reason: ModalReason) {
    this.showLoading();
    const options = {
      subject: 'all',
      entityOptions: { exPath: 'delivery' }
    };
    this.service.postSnsDeliveryRequest(this._snsDeliveryRequest, options)
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe(
        (response) => {
          this.hideLoading();
          const snsBulkSendHistory = <ISnsBulkSendHistory>response;
          if (snsBulkSendHistory.totalCount === 0) {
            this.dialogService.info('送信データがありませんでした。').subscribe(dialogRef => {
              dialogRef.hide();
            });
          } else {
            // 操作履歴ログ設定
            this.service.postOperationHistoryLogLineBulkSend(
              this._snsSendMessageInfo.dmExtractMenuDiv, this._snsSendMessageInfo.snsSendTitle);

            const dialogMessage = StringUtils.concat(
              '一斉送信を実行しました。（該当件数：',
              String(snsBulkSendHistory.totalCount),
              '件）\n',
              '※送信結果は一斉送信履歴画面でご確認下さい',
            );
            this.dialogService.info(dialogMessage).subscribe(dialogRef => {
              dialogRef.hide();
              this.emitDismissEvent(reason);
            });
          }
        },
        error => {
          if (error && error.status === 504) {
            // 504エラーのときは下記ダイアログを表示
            const dialogMessage = '一斉送信を実行しました。\n※送信結果は一斉送信履歴画面でご確認下さい';
            this.dialogService.info(dialogMessage).subscribe(dialogRef => {
              dialogRef.hide();
              this.emitDismissEvent(reason);
            });
          } else {
            throw error;
          }
        }
      );
  }

  /**
   * モーダルウィンドウ終了処理
   * @param reason
   */
  dismiss(reason: ModalReason): void {
    // 確定以外の場合はそのまま通知
    if (reason !== ModalReason.Done) {
      this.emitDismissEvent(reason);
      return;
    }

    // ロールでの送信可否判定
    this.service.isAvailableLineBulkSendRole().subscribe((responses) => {
      const sendRole = responses;
      if (!sendRole) {
        this.dialogService
          .info('権限がないため送信できません。')
          .subscribe((modalRef) => {
            modalRef.hide();
          });
        return;
      }
      // LINE一斉送信
      this.broadcastSns(reason);
    });
  }

  /**
   * リッチ版LINEテンプレートを設定するかを判定する
   * @returns true:リッチ版LINEテンプレートを設定する、 false:リッチ版LINEテンプレートを設定しない
   */
  private setSettingLineRichTemplate() {
    const entity = this._snsSendMessageInfo.dmContentInfo;
    this._isSettingLineRichTemplate = entity.dmExtractMenuCode === DmExtractMenuCode.Customize ||
      entity.dmExtractMenuCode === DmExtractMenuCode.Other;
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/sns-send-message-confirm-modal/sns-send-message-confirm-modal.component.ts